import { normalizeChipCode } from './chip-code';

/**
 * Qualità della corrispondenza. Più basso è meglio:
 * - `0` codice identico alla query
 * - `1` codice che inizia con la query
 * - `2` codice che contiene la query
 */
export type MatchScore = 0 | 1 | 2;

export type ChipMatch<T> = {
  chip: T;
  score: MatchScore;
  /** Intervalli `[inizio, fine)` da evidenziare, indici su `chip.code`. */
  ranges: [number, number][];
};

/**
 * Ricerca un chip per codice, pensata per rispondere a ogni tasto premuto.
 *
 * La ricerca lavora su una lista già in memoria (nessuna chiamata di rete), è
 * case-insensitive e ignora spazi e simboli: la query viene normalizzata con la
 * stessa funzione usata in fase di salvataggio, quindi `ab 12-34` trova `AB1234`.
 *
 * Si cerca **solo per codice**, di proposito: è il flusso del lettore barcode, e
 * un match per nome renderebbe ambiguo il caso "il codice non esiste, lo aggiungo
 * a mano?".
 *
 * @param chips lista di chip con almeno la proprietà `code` (già normalizzata)
 * @param query testo digitato dall'operatore
 * @param limit numero massimo di risultati restituiti
 */
export function findChipMatches<T extends { code: string }>(
  chips: readonly T[],
  query: string,
  limit = 100,
): ChipMatch<T>[] {
  const needle = normalizeChipCode(query);
  if (needle.length === 0) return [];

  const matches: ChipMatch<T>[] = [];

  for (const chip of chips) {
    const at = chip.code.indexOf(needle);
    if (at === -1) continue;

    const score: MatchScore = at === 0 ? (chip.code.length === needle.length ? 0 : 1) : 2;
    matches.push({ chip, score, ranges: [[at, at + needle.length]] });
  }

  // A parità di qualità l'ordine è alfabetico, così la lista non "salta"
  // mentre l'operatore continua a digitare.
  matches.sort((a, b) => a.score - b.score || compareCodes(a.chip.code, b.chip.code));

  return matches.slice(0, limit);
}

function compareCodes(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Sceglie il chip su cui agire quando l'operatore preme Invio.
 *
 * Con un solo risultato è quello. Con più risultati si accetta solo una
 * corrispondenza esatta: se l'operatore ha scansionato il codice completo, il
 * lettore barcode aggiunge spesso un invio automatico e ci si aspetta che il
 * chip giusto venga chiuso senza dover toccare lo schermo. In tutti gli altri
 * casi l'Invio non fa nulla e l'operatore sceglie a mano — meglio un no-op che
 * chiudere il chip sbagliato.
 */
export function pickForEnter<T extends { code: string }>(
  matches: readonly ChipMatch<T>[],
): T | null {
  if (matches.length === 1) return matches[0]!.chip;
  const exact = matches.find((m) => m.score === 0);
  return exact ? exact.chip : null;
}
